/**
 * useShareAnalyticsProducts
 *
 * Fetches and hydrates analytics products for the share view.
 * This replaces useSetAnalyticsProducts from the main app, avoiding
 * the dependency on selectedExperience from Redux.
 */

import { useEffect } from "react";

import { GetAnalyticsProductsResponseBodyType } from "@intelligems/ig-types/functions/analytics-products/get-analytics-products";
import { api } from "@src/api/api";
import { ExcludeS3Response } from "@src/api/types/api";
import { maybeLoadS3Json } from "@src/api/utils/loadJson";
import { useDispatch } from "@src/store";
import { initAnalyticsProducts } from "@src/store/slices/analyticsProducts";
import { useQuery } from "@tanstack/react-query";

const fetchShareAnalyticsProducts = async (
  experienceId: string
): Promise<ExcludeS3Response<GetAnalyticsProductsResponseBodyType>> => {
  const resp = await api.get<GetAnalyticsProductsResponseBodyType>(
    `/v2/experiences/${experienceId}/analytics-products`
  );
  return maybeLoadS3Json(resp.data);
};

export const useShareAnalyticsProducts = (experienceId: string | undefined) => {
  const dispatch = useDispatch();

  const query = useQuery({
    queryKey: ["share-analytics-products", experienceId],
    queryFn: async () => fetchShareAnalyticsProducts(experienceId!),
    enabled: Boolean(experienceId),
    retry: false,
    meta: {
      onError: {
        401: "ignore",
        403: "ignore",
        404: "ignore",
        any: "ignore",
      },
    },
  });

  const { data, isSuccess, isLoading, isFetching } = query;

  useEffect(() => {
    dispatch(
      initAnalyticsProducts({
        isSuccess,
        isLoading,
        isFetching,
        analyticsProducts: data?.products ?? [],
      })
    );
  }, [isSuccess, isFetching, isLoading, data, dispatch]);

  return query;
};
